/**
 * Create or edit a product line. Three columns: details + texture, commercial
 * name translations, available stores (with a warning on stores without any
 * workshop).
 */
import { useState, type SubmitEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import type { Store } from '@/shared/config/app-config';
import { appConfigQueryKey, fetchAppConfig } from '@/shared/config/app-config';
import { Dialog } from '@/shared/ui/Dialog';
import { PictureField } from '@/shared/ui/PictureField';
import { usePicture } from '@/shared/ui/use-picture';
import { CheckboxTree } from '@/shared/ui/checkbox-tree/CheckboxTree';
import {
  createProductLine,
  deleteProductLinePicture,
  updateProductLine,
  uploadProductLinePicture,
  type ProductLine,
} from './api';
import {
  emptyProductLineForm,
  formToProductLine,
  initialTranslations,
  productLinePictureUrl,
  productLineToForm,
  SAV_OPTIONS,
  SHADOW_OPTIONS,
  SIZE_DISPLAY_OPTIONS,
  storeLinks,
  storeWarning,
  validateProductLineForm,
  type ProductLineFormErrors,
  type ProductLineFormValues,
} from './product-line-form';
import { Select } from '@/shared/ui/Select';
import { kebabCase } from '@/shared/lib/kebab-case';

export interface ProductLineDialogProps {
  item: ProductLine | null;
  stores: readonly Store[];
  languages: readonly string[];
  onClose: (result: 'Confirm' | 'Cancel') => void;
}

export function ProductLineDialog({
  item,
  stores,
  languages,
  onClose,
}: Readonly<ProductLineDialogProps>) {
  const creating = !item;
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ProductLineFormValues>(() =>
    item ? productLineToForm(item) : emptyProductLineForm(),
  );
  const [translations, setTranslations] = useState<Record<string, string>>(() =>
    initialTranslations(item),
  );
  const [selectedStores, setSelectedStores] = useState<string[]>(
    () => item?.stores ?? [],
  );
  const texture = usePicture();
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<ProductLineFormErrors>({});
  // Red border once a field has been edited and is invalid.
  const [dirty, setDirty] = useState<Set<keyof ProductLineFormValues>>(
    () => new Set(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const sortedLanguages = [...languages].sort((a, b) => a.localeCompare(b));
  const rows = storeLinks(stores);
  const productLineHasWorkshop = !!item?.workshopId;

  const set = <K extends keyof ProductLineFormValues>(
    key: K,
    value: ProductLineFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setDirty((prev) => new Set(prev).add(key));
  };
  const liveErrors = validateProductLineForm(values);
  const invalidClass = (key: keyof ProductLineFormValues) =>
    dirty.has(key) && liveErrors[key] ? 'invalid' : '';
  const fieldError = (key: keyof ProductLineFormErrors) =>
    submitted && errors[key];
  const testId = (key: string) => `product-line-form-${kebabCase(key)}`;

  const save = useMutation({
    mutationFn: async () => {
      const data = formToProductLine(
        values,
        selectedStores,
        translations,
        Date.now(),
      );
      let id: string | undefined;
      try {
        if (item) {
          await updateProductLine({ ...data, id: item.id });
          id = item.id;
        } else {
          const created = await createProductLine(data);
          id = created?.id;
        }
      } catch (error) {
        throw new Error(
          creating
            ? 'Failed to create product line'
            : 'Failed to update product line',
          { cause: error },
        );
      }
      try {
        if (texture.touched && id) {
          if (texture.file)
            await uploadProductLinePicture(id, texture.file, 'texture');
          else await deleteProductLinePicture(id, 'texture');
        }
      } catch (error) {
        throw new Error('Failed to upload product line pictures', {
          cause: error,
        });
      }
      await fetchAppConfig();
      await queryClient.invalidateQueries({ queryKey: appConfigQueryKey });
    },
    onSuccess: () => onClose('Confirm'),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const onSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setErrorMessage(null);
    const nextErrors = validateProductLineForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setErrorMessage('Please fix the errors');
      return;
    }
    save.mutate();
  };

  const text = (
    key: 'productLine' | 'commercialProductLine' | 'display' | 'productGroup',
    label: string,
    placeholder: string,
  ) => (
    <div className="mb-4">
      <label className="field-label" htmlFor={`product-line-${key}`}>
        {label}
      </label>
      <input
        id={`product-line-${key}`}
        data-testid={testId(key)}
        className={`field ${invalidClass(key)}`}
        name={key}
        placeholder={placeholder}
        value={values[key]}
        onChange={(event) => set(key, event.target.value)}
      />
      <small hidden={!fieldError(key)} data-testid={`${testId(key)}-error`}>
        {errors[key]}
      </small>
    </div>
  );

  const select = (
    key: 'sav' | 'sizeDisplay' | 'shadowType',
    label: string,
    placeholder: string,
    options: readonly { value: string; label: string }[],
  ) => (
    <div className="mb-4">
      <label className="field-label" htmlFor={`product-line-${key}`}>
        {label}
      </label>
      <div className={`field field-selector ${invalidClass(key)}`}>
        <Select
          id={`product-line-${key}`}
          name={key}
          placeholder={placeholder}
          options={options}
          value={values[key]}
          onChange={(value) =>
            set(key, value as ProductLineFormValues[typeof key])
          }
          testId={testId(key)}
        />
      </div>
      <small hidden={!fieldError(key)} data-testid={`${testId(key)}-error`}>
        {errors[key]}
      </small>
    </div>
  );

  const warning = (message: string | null) =>
    message ? (
      <span title={message} className="ml-2 text-[orange]">
        <FontAwesomeIcon icon={faExclamationCircle} />
        <span className="sr-only">{message}</span>
      </span>
    ) : null;

  return (
    <Dialog
      title={creating ? 'Product line creation' : 'Product line edit'}
      onClose={() => onClose('Cancel')}
      width="1200px"
      testId="product-line-dialog"
    >
      <form className="form popin-form" onSubmit={onSubmit} noValidate>
        <div className="bs-row">
          <div className="col-span-5">
            <div className="bs-row">
              <div className="col-span-6">
                {text('productLine', 'Product Line', 'Reference')}
                {text(
                  'commercialProductLine',
                  'Commercial Product Line',
                  'Reference',
                )}
                {text('display', 'Display', 'Display order')}
                {text('productGroup', 'Product Group', 'Product group')}
                {select(
                  'sav',
                  'Personalization Post Purchase Allow',
                  '--Select post purchase allow--',
                  SAV_OPTIONS,
                )}
                {select(
                  'sizeDisplay',
                  'Size Display',
                  '--Select size display--',
                  SIZE_DISPLAY_OPTIONS,
                )}
                {errorMessage && (
                  <div
                    className="alert mb-4"
                    role="alert"
                    data-testid="product-line-form-error"
                  >
                    <span className="error-msg">{errorMessage}</span>
                  </div>
                )}
                <div className="mt-2 flex justify-center">
                  <button
                    className="button button-form"
                    type="submit"
                    disabled={save.isPending}
                    data-testid="product-line-form-save-button"
                  >
                    Save
                  </button>
                </div>
              </div>
              <div className="col-span-6">
                <PictureField
                  id="product-line-texture"
                  name="texturePicture"
                  label="Texture"
                  picture={texture}
                  serverUrl={productLinePictureUrl(item?.id, 'texture')}
                  testId="product-line-form-texture"
                />
                {select(
                  'shadowType',
                  'Shadow Type',
                  '--Select shadow type--',
                  SHADOW_OPTIONS,
                )}
              </div>
            </div>
          </div>

          <div className="col-span-3">
            <div className="field-label" id="product-line-translations-label">
              Commercial Product Line Translations
            </div>
            <div className="table-container">
              <div
                className="items-container"
                style={{ maxHeight: 420, overflow: 'auto' }}
                role="region"
                aria-labelledby="product-line-translations-label"
              >
                <table className="table items">
                  <thead>
                    <tr>
                      <th style={{ width: '20%', padding: '0.5rem 1.5rem' }}>
                        Language
                      </th>
                      <th style={{ width: '80%', padding: '0.5rem 1.5rem' }}>
                        Translation
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedLanguages.map((language) => (
                      <tr
                        key={language}
                        data-testid="product-line-form-translation-row"
                        data-value={language}
                      >
                        <td style={{ width: '40%' }} title={language}>
                          {language}
                        </td>
                        <td
                          style={{ width: '60%' }}
                          title={translations[language] ?? ''}
                        >
                          <input
                            className="field"
                            aria-label={`Translation ${language}`}
                            data-testid="product-line-form-translation-input"
                            value={translations[language] ?? ''}
                            onChange={(event) =>
                              setTranslations((prev) => ({
                                ...prev,
                                [language]: event.target.value,
                              }))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="col-span-4 store-list">
            <span className="field-label">Available in store</span>
            <div
              className="checkbox-group"
              role="region"
              aria-label="Available in store"
            >
              <CheckboxTree
                rows={rows}
                breakdown={['region', 'country']}
                idKey="id"
                labelKey="label"
                selectedIds={selectedStores}
                onChange={(ids) => setSelectedStores(ids.map(String))}
                testId="product-line-form-stores"
                renderLeafRight={(row) =>
                  warning(
                    storeWarning(stores, productLineHasWorkshop, {
                      level: 2,
                      storeId: row.id,
                    }),
                  )
                }
                renderBranchRight={(branch, level) =>
                  warning(
                    storeWarning(stores, productLineHasWorkshop, {
                      level,
                      country: branch.label,
                    }),
                  )
                }
              />
            </div>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
